import { Box, Button, Container, Grid, Typography } from '@mui/material';
import { Add, ArrowForwardIos } from '@mui/icons-material';
import { useStyles } from './styles';
import LazyLoad from 'react-lazyload';
import CustomButton from 'components/CustomButton';
import useIsMobile from 'hooks/useIsMobile';
import EmbedVideo from 'components/EmbedVideo';
import Faq from 'components/SeasonBreak/Faq';
import { useRouter } from 'next/router';
import ReactGA from 'react-ga4';
import { isUserFromJournal } from 'utils';
import { UserTypeEnum } from 'enums';
import { useAuthUser } from 'features/auth/AuthUserContext';

export default function MainContent() {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const router = useRouter();
  const { user, isAuthenticated, profile } = useAuthUser();
  const userFromJournal = isUserFromJournal(profile);
  const isSubscriberUserOrJournal =
    (isAuthenticated && user.tp_user !== UserTypeEnum.TRIAL) || userFromJournal;
  const currentDate = new Date().getTime();
  const seasonBreakActive = currentDate < new Date('2023-07-31T23:59:00').getTime();

  function handleButtonClick() {
    ReactGA.event({
      category: 'landing_page',
      action: 'season_break_landing_page_button_click',
      label: 'Click on any button on the page',
      value: user.id,
    });

    router.push('/pricing?promotion=season_break');
  }

  return (
    <>
      <Box className={classes.bgHeader}>
        <Container>
          <Box pt={6} pb={7}>
            <Grid container={true} spacing={2} alignItems="center" justifyContent="center">
              <Grid className={classes.whiteColor} align="left" item={true} xs={12} md={4}>
                <div className={classes.contentLeft}>
                  <Typography align="justify" component="h1" variant="h4">
                    Mind the Graph <br /> SEASON BREAK
                  </Typography>

                  <Typography component="h2" variant="h3">
                    <b>
                      3 months <br /> From <del>75 USD</del> <br /> to 29 USD*
                    </b>
                  </Typography>

                  <Typography variant="body2">
                    *valid for new subscribers for a limited time.
                  </Typography>
                </div>
              </Grid>

              <Grid item={true} xs={12} md={4} align={!isMobile ? 'left' : 'center'}>
                <img
                  src="/images/season-break/season-break-last-days-image.png"
                  alt="header illustration image"
                  width={!isMobile ? 468 : 300}
                  height={!isMobile ? 323 : 220}
                />
              </Grid>

              <Grid item={true} xs={12} md={4} align="left">
                <div className={classes.contentRight}>
                  <div className={classes.plusInformation}>
                    <Add className={classes.whiteColor} fontSize="large" />

                    <Typography className={classes.whiteColor} component="p" variant="h5">
                      <b>
                        get a custom <br /> avatar of yourself
                      </b>
                    </Typography>
                  </div>

                  <Button
                    className={classes.buttonWhite}
                    variant="contained"
                    disabled={isSubscriberUserOrJournal || !seasonBreakActive}
                    onClick={handleButtonClick}
                  >
                    <b>Subscribe Now</b>
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {!isMobile && (
        <LazyLoad offset={100}>
          <Box className={classes.bgContainerSeason}>
            <Container className={classes.negativeContainer}>
              <Box className={classes.boxSeasonBreak} px={10} mx={1} py={5}>
                <Typography className={classes.featuresBoxInformation} variant="h4" component="h2">
                  <b>It's Season Break Time!</b>
                </Typography>

                <Box mt={3}>
                  <Typography className={classes.graphiteColor} component="p" variant="h6">
                    Nothing like a break and Mind the Graph to unleash your creative potential.{' '}
                    Thinking of that, we offer you a <br /> limited time opportunity to get 3 months{' '}
                    on a premium plan for the price of 1. Get inspired transforming your <br /> data{' '}
                    into beautiful graphs with minimal effort.
                  </Typography>
                </Box>

                <Box textAlign="center" mt={3}>
                  <CustomButton
                    variant="contained"
                    disabled={isSubscriberUserOrJournal || !seasonBreakActive}
                    onClick={handleButtonClick}
                  >
                    Enjoy the Season Break
                  </CustomButton>
                </Box>
              </Box>
            </Container>
          </Box>

          <Box bgcolor="#EEEEEE">
            <Container>
              <Box display="flex" justifyContent="center" alignItems="center">
                <Box mr={4}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Add className={classes.primaryColor} />

                    <Typography className={classes.primaryColor} component="h2" variant="h4">
                      <b>Get a custom avatar</b>
                    </Typography>
                  </Box>

                  <Typography className={classes.graphiteColor} component="p" variant="h6">
                    <b>Enjoy the Season Break offer:</b> <br /> 3-month subscription for only 29 USD{' '}
                    <br /> + get a custom avatar of yourself for <br /> your posters, presentations
                    and other <br /> science designs.
                  </Typography>
                </Box>

                <img
                  src="/images/season-break/picture-avatar.png"
                  alt="picture avatar image"
                  loading="lazy"
                  width={649}
                  height={309}
                />
              </Box>

              <Box textAlign="center" py={5}>
                <CustomButton
                  variant="contained"
                  disabled={isSubscriberUserOrJournal || !seasonBreakActive}
                  onClick={handleButtonClick}
                >
                  Get My Avatar
                </CustomButton>
              </Box>
            </Container>
          </Box>
        </LazyLoad>
      )}

      <LazyLoad offset={100}>
        <Container>
          <Box py={6} pl={isMobile ? 5 : 11} textAlign="left">
            <Typography className={classes.featuresBoxInformation} variant="h4" component="h2">
              <b>3 Months to Enjoy all Features</b>
            </Typography>

            <Typography className={classes.featuresBoxSubtitle} component="p" variant="h5">
              All premium features available for you during the Season Break.
            </Typography>

            <div className={classes.featureGrid}>
              <div className={classes.featureItem}>
                <img
                  src="/images/unlimited-scientific-illustrations-icon.svg"
                  alt="Unlimited scientific illustrations icon"
                  loading="lazy"
                  width={50}
                  height={60}
                />

                <Typography component="p" variant="h6">
                  75,000+ <br /> watermark-free <br /> scientific illustrations
                </Typography>
              </div>

              <div className={classes.featureItem}>
                <img
                  src="/images/full-copyright-icon.svg"
                  alt="full copyright icon"
                  loading="lazy"
                  width={50}
                  height={50}
                />

                <Typography component="p" variant="h6">
                  Full Copyright <br /> to Publish
                </Typography>
              </div>

              <div className={classes.featureItem}>
                <img
                  src="/images/priority-support-icon.svg"
                  alt="Priority support icon"
                  loading="lazy"
                  width={50}
                  height={50}
                />

                <Typography component="p" variant="h6">
                  5 custom <br /> illustration request <br /> per month
                </Typography>
              </div>
            </div>

            <div className={classes.featureGrid}>
              <div className={classes.featureItem}>
                <img
                  src="/images/download-formats-icon.svg"
                  alt="Download formats icon"
                  loading="lazy"
                  width={50}
                  height={37}
                />

                <Typography component="p" variant="h6">
                  Download in PNG, <br /> PDF and TIFF
                </Typography>
              </div>

              <div className={classes.featureItem}>
                <img
                  src="/images/unlimited-creations-icon.svg"
                  alt="Unlimited creations icon"
                  loading="lazy"
                  width={50}
                  height={50}
                />

                <Typography component="p" variant="h6">
                  Unlimited <br /> creations
                </Typography>
              </div>

              <div className={classes.featureItem}>
                <img
                  src="/images/and-more-icon.svg"
                  alt="and more icon"
                  loading="lazy"
                  width={50}
                  height={50}
                />

                <Typography component="p" variant="h6">
                  And much <br /> more
                </Typography>
              </div>
            </div>

            <Box textAlign="center" pt={6}>
              <CustomButton
                variant="contained"
                disabled={isSubscriberUserOrJournal || !seasonBreakActive}
                onClick={handleButtonClick}
              >
                Get 3 months for 29 USD
              </CustomButton>
            </Box>
          </Box>
        </Container>
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box bgcolor="#EEEEEE">
          <Container>
            <Box pl={isMobile ? 5 : 11} textAlign="left" py={6}>
              <Typography className={classes.featuresBoxInformation} variant="h4" component="h2">
                <b>How does avatar creation work?</b>
              </Typography>

              <Typography className={classes.featuresBoxSubtitle} component="p" variant="h5">
                Follow the steps to learn how the process works.
              </Typography>

              <div className={classes.featureGrid}>
                <div className={classes.featureItemAvatarCreation}>
                  <Box display="flex" alignItems="center">
                    <ArrowForwardIos className={classes.primaryColor} />

                    <Typography className={classes.primaryColor} component="p" variant="h3">
                      <b>01</b>
                    </Typography>
                  </Box>

                  <Box display="block">
                    <Typography component="p" variant="h6">
                      Sign Up to a <br /> 3-month <br /> subscription at <br /> Mind the Graph
                    </Typography>
                  </Box>
                </div>

                <div className={classes.featureItemAvatarCreation}>
                  <Box display="flex" alignItems="center">
                    <ArrowForwardIos className={classes.primaryColor} />

                    <Typography className={classes.primaryColor} component="p" variant="h3">
                      <b>02</b>
                    </Typography>
                  </Box>

                  <Box display="block">
                    <Typography component="p" variant="h6">
                      Send us at <br /> least 3 of your <br /> best face pics
                    </Typography>
                  </Box>
                </div>

                <div className={classes.featureItemAvatarCreation}>
                  <Box display="flex" alignItems="center">
                    <ArrowForwardIos className={classes.primaryColor} />

                    <Typography className={classes.primaryColor} component="p" variant="h3">
                      <b>03</b>
                    </Typography>
                  </Box>

                  <Box display="block">
                    <Typography component="p" variant="h6">
                      We'll deliver <br /> a preview after <br /> 7 working days
                    </Typography>
                  </Box>
                </div>

                <div className={classes.featureItemAvatarCreation}>
                  <Box display="flex" alignItems="center">
                    <ArrowForwardIos className={classes.primaryColor} />

                    <Typography className={classes.primaryColor} component="p" variant="h3">
                      <b>04</b>
                    </Typography>
                  </Box>

                  <Box display="block">
                    <Typography component="p" variant="h6">
                      Adjustments* <br /> request <br /> (if needed)
                    </Typography>
                  </Box>
                </div>

                <div className={classes.featureItemAvatarCreation}>
                  <Box display="flex" alignItems="center">
                    <ArrowForwardIos className={classes.primaryColor} />

                    <Typography className={classes.primaryColor} component="p" variant="h3">
                      <b>05</b>
                    </Typography>
                  </Box>

                  <Box display="block">
                    <Typography component="p" variant="h6">
                      Customize <br />
                      creations with <br /> your own <br /> illustrated avatar
                    </Typography>
                  </Box>
                </div>
              </div>

              <Box pt={4}>
                <Typography className={classes.graphiteColor} variant="body2">
                  *You'll have up to 3 adjustments requests to make the illustration look just like
                  you imagined.
                </Typography>

                <Box textAlign="center" pt={6}>
                  <CustomButton
                    variant="contained"
                    disabled={isSubscriberUserOrJournal || !seasonBreakActive}
                    onClick={handleButtonClick}
                  >
                    Get My Avatar
                  </CustomButton>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </LazyLoad>

      {!isMobile && (
        <LazyLoad offset={100}>
          <Box bgcolor="#5F29CC">
            <Container>
              <Box py={6} display="flex" justifyContent="center" alignItems="center">
                <Box>
                  <Typography
                    variant="h4"
                    component="h2"
                    className={classes.featuresBoxInformationWhite}
                  >
                    <b>Meet the New Workspace</b>
                  </Typography>

                  <Box pt={4}>
                    <Typography
                      className={classes.featuresBoxSubtitleWhite}
                      component="p"
                      variant="h5"
                    >
                      New features for a whole <br /> new experience. The best science <br />{' '}
                      infographic maker got some <br /> upgrades. Check them out!
                    </Typography>
                  </Box>

                  <Box py={6}>
                    <Button
                      className={classes.buttonWhite}
                      variant="contained"
                      disabled={isSubscriberUserOrJournal || !seasonBreakActive}
                      onClick={handleButtonClick}
                    >
                      <b>Subscribe Now to Explore</b>
                    </Button>
                  </Box>
                </Box>

                <Box ml={11} pt={4}>
                  <EmbedVideo
                    classes={{ root: classes.video }}
                    url="https://www.youtube.com/embed/Y2YMnuQPTFA"
                    width="550"
                  />
                </Box>
              </Box>
            </Container>
          </Box>
        </LazyLoad>
      )}

      <LazyLoad offset={100}>
        <Container>
          <Box bgcolor="#FAFAFA" py={4} pl={isMobile ? 0 : 4.3}>
            <Faq />
          </Box>
        </Container>
      </LazyLoad>
    </>
  );
}
